import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import type { TourWithProgress } from 'types/tour';

interface TourContextValue {
  /** Pending tours for the current route (not yet completed or new version) */
  pendingTours: TourWithProgress[];
  /** All tours for the current route (including completed) */
  allTours: TourWithProgress[];
  /** Whether the tour engine is currently running */
  isRunning: boolean;
  /** Number of pending tours (for badge) */
  pendingCount: number;
  /** Update tours from external fetch (called by TourProvider) */
  setPendingTours: (tours: TourWithProgress[]) => void;
  setAllTours: (tours: TourWithProgress[]) => void;
  /** Start a specific tour by ID */
  startTour: (tourId: string) => void;
  /** Register the Driver.js start function from TourProvider */
  registerStartFn: (fn: (tourId: string) => void) => void;
  setIsRunning: (running: boolean) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const [pendingTours, setPendingTours] = useState<TourWithProgress[]>([]);
  const [allTours, setAllTours] = useState<TourWithProgress[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const startFnRef = useRef<((tourId: string) => void) | null>(null);

  const registerStartFn = useCallback((fn: (tourId: string) => void) => {
    startFnRef.current = fn;
  }, []);

  const startTour = useCallback((tourId: string) => {
    if (startFnRef.current) {
      startFnRef.current(tourId);
    }
  }, []);

  return (
    <TourContext.Provider
      value={{
        pendingTours,
        allTours,
        isRunning,
        pendingCount: pendingTours.length,
        setPendingTours,
        setAllTours,
        startTour,
        registerStartFn,
        setIsRunning,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTourContext = (): TourContextValue => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTourContext must be used inside TourProvider');
  return ctx;
};
